
import { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const IntestacyLawsForm = ({
  formId,
  title,
  subtitle,
}: {
  formId: string;
  title: string;
  subtitle: string;
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    situation: '',
    timeline: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('propertyAddress', formData.propertyAddress);
      formBody.append('situation', formData.situation);
      formBody.append('timeline', formData.timeline);
      formBody.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d67952ua728k8ctu1pd0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
          <i className="ri-check-line text-3xl text-teal-600"></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600">
          We&apos;ve received your information and will contact you within 24
          hours to discuss your property situation.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-100">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{subtitle}</p>
      </div>

      <form
        id={formId}
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="(915) 555-0123"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Property Address
          </label>
          <input
            type="text"
            name="propertyAddress"
            value={formData.propertyAddress}
            onChange={(e) =>
              setFormData({ ...formData, propertyAddress: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="Address of the property"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Situation
            </label>
            <select
              name="situation"
              value={formData.situation}
              onChange={(e) =>
                setFormData({ ...formData, situation: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select situation</option>
              <option value="No Will Exists">No will exists</option>
              <option value="Intestate Estate">Dealing with intestate estate</option>
              <option value="Multiple Heirs">Multiple heirs involved</option>
              <option value="Property Distribution">Need help with property distribution</option>
              <option value="Spouse Inheritance">Spouse inheritance questions</option>
              <option value="Children Inheritance">Children inheritance questions</option>
              <option value="Want to Sell Property">Want to sell inherited property</option>
              <option value="Other">Other situation</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timeline
            </label>
            <select
              name="timeline"
              value={formData.timeline}
              onChange={(e) =>
                setFormData({ ...formData, timeline: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select timeline</option>
              <option value="ASAP">Need help ASAP</option>
              <option value="1-3 months">Within 1-3 months</option>
              <option value="3-6 months">Within 3-6 months</option>
              <option value="6+ months">6+ months</option>
              <option value="Just exploring">Just exploring options</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tell Us More
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={(e) =>
              setFormData({
                ...formData,
                message: e.target.value.slice(0, 500),
              })
            }
            rows={3}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
            placeholder="Tell us about your situation and how we can help..."
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.message.length}/500 characters
          </p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Submitting...
            </span>
          ) : (
            'Get Your Free Consultation'
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          Your information is secure and will never be sold. We&apos;re here to
          help.
        </p>
      </form>
    </div>
  );
};

export default function TexasIntestacyLawsPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Texas Intestacy Laws Explained | What Happens When There's No Will | TREX RE LLC"
        description="Comprehensive guide to Texas intestacy laws. Learn how assets are distributed when someone dies without a will, including community property rules, spousal rights, children's inheritance, and what happens to your El Paso property."
        keywords="Texas intestacy laws, dying without a will Texas, intestate succession Texas, community property Texas, spouse inheritance Texas, children inheritance no will, El Paso estate distribution, Texas estate laws"
        canonical="/elpaso-tx/texas-intestacy-laws"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Texas Intestacy Laws Explained: What Happens When There Is No Will',
          description:
            'Comprehensive guide explaining Texas intestacy laws, how assets are distributed without a will, community property rules, and inheritance rights.',
          author: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/5ffba6a3a21fd1d0f2d98005617506a7.jpg)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/35"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-500/90 text-white text-sm font-semibold rounded-full mb-6">
                <i className="ri-scales-3-line mr-2"></i>
                Texas Estate Law Guide
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Texas <span className="text-teal-400">Intestacy Laws</span> Explained
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                When someone dies without a valid will, Texas law determines who inherits their
                property. Understanding these intestacy laws is crucial for protecting your
                family&apos;s interests and your inherited assets.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center text-white">
                  <i className="ri-checkbox-circle-line text-teal-400 mr-2"></i>
                  <span>Community Property Rules</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-checkbox-circle-line text-teal-400 mr-2"></i>
                  <span>Spousal Rights</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-checkbox-circle-line text-teal-400 mr-2"></i>
                  <span>Children&apos;s Inheritance</span>
                </div>
              </div>
            </div>

            <div>
              <IntestacyLawsForm
                formId="hero-intestacy-form"
                title="Need Help With an Intestate Estate?"
                subtitle="We buy inherited properties and help families navigate complex situations"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When a loved one passes away without leaving a valid will, the situation can feel
            overwhelming. Beyond the grief and emotional turmoil, families suddenly find
            themselves facing complex legal questions: Who inherits the house? How are bank
            accounts divided? What happens to personal property? These questions become even
            more complicated when you realize that <strong>Texas law — not your family&apos;s wishes — will determine the answers</strong>.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This situation is called &quot;intestate succession,&quot; and it affects more families
            than you might think. According to various surveys, roughly 60% of Americans don&apos;t
            have a will. When these individuals pass away, their estates must be distributed
            according to state law, which may or may not align with what they would have wanted.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            In this comprehensive guide, we&apos;ll break down Texas intestacy laws in plain
            English. You&apos;ll learn exactly how the state distributes assets when there&apos;s
            no will, understand the critical difference between community and separate property,
            and discover what options exist for families dealing with intestate estates. Whether
            you&apos;re currently facing this situation or simply want to understand what could
            happen to your own estate, this information is essential for every Texas resident.
            For a deeper understanding of{' '}
            <a
              href="/elpaso-tx/what-happens-if-you-dont-probate-will-texas"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              what happens if you don&apos;t probate a will in Texas
            </a>
            , including the four-year deadline and other consequences, see our related guide.
          </p>
        </div>

        {/* What Is Intestate Succession Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What Exactly Is Intestate Succession?
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=legal%20scales%20of%20justice%20with%20texas%20state%20outline%20and%20family%20tree%20diagram%20showing%20inheritance%20distribution%20paths%20professional%20legal%20illustration%20style%20warm%20earth%20tones%20clean%20modern%20design&width=800&height=500&seq=intestacy-definition-002&orientation=landscape"
            alt="Legal scales representing Texas intestate succession"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            &quot;Intestate&quot; simply means dying without a valid will. When this happens,
            the deceased person is said to have died &quot;intestate,&quot; and their estate goes
            through &quot;intestate succession&quot; — a legal process where the state determines
            who inherits based on predetermined rules.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Think of intestacy laws as the state&apos;s &quot;default will&quot; for everyone.
            Texas legislators created these rules to provide a fair and orderly way to
            distribute assets when someone hasn&apos;t made their own arrangements. The laws
            prioritize close family members — spouses, children, parents, and siblings —
            based on the assumption that most people would want their assets to go to their
            nearest relatives.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            However, here&apos;s the critical point: <strong>intestacy laws are one-size-fits-all</strong>.
            They don&apos;t account for family dynamics, estrangements, special needs, or
            personal preferences. The state doesn&apos;t know that you wanted your best friend
            to have your car, or that you&apos;ve been estranged from a sibling for twenty years,
            or that one child needs more financial support than another. Without a will,
            these nuances are ignored.
          </p>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-information-line mr-2 text-amber-500"></i>
              Important Distinction
            </h4>
            <p className="text-gray-700">
              Intestacy laws only apply to &quot;probate assets&quot; — property that would
              normally pass through a will. Assets with designated beneficiaries (like life
              insurance or retirement accounts) or property held in joint tenancy pass
              directly to the named beneficiary or surviving owner, regardless of intestacy
              laws.
            </p>
          </div>
        </section>

        {/* Community vs Separate Property Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The Foundation: Community Property vs. Separate Property
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Before we can understand how Texas distributes an intestate estate, we need
            to understand a fundamental concept: <strong>Texas is a community property
            state</strong>. This means that property owned by married couples is classified
            into two categories, and each category is treated differently under intestacy laws.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=split%20image%20showing%20married%20couple%20with%20shared%20home%20and%20assets%20on%20one%20side%20and%20individual%20person%20with%20personal%20belongings%20on%20other%20side%20representing%20community%20versus%20separate%20property%20concept%20professional%20photography%20warm%20lighting&width=800&height=500&seq=intestacy-property-003&orientation=landscape"
            alt="Community property versus separate property illustration"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            What Is Community Property?
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Community property includes most assets acquired by either spouse during the
            marriage. The key word is &quot;during&quot; — it doesn&apos;t matter whose name is on the
            account or who earned the money. If it was acquired while married, it&apos;s presumed
            to be community property.
          </p>

          <div className="bg-teal-50 p-6 rounded-lg mb-6">
            <h4 className="font-bold text-gray-900 mb-3">Examples of Community Property:</h4>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-1"></i>
                <span>Wages and salaries earned during marriage</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-1"></i>
                <span>Real estate purchased during marriage (even if only one spouse&apos;s name is on the deed)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-1"></i>
                <span>Vehicles bought during marriage</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-1"></i>
                <span>Retirement benefits earned during marriage</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-1"></i>
                <span>Business interests developed during marriage</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-1"></i>
                <span>Investment returns on community property</span>
              </li>
            </ul>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            What Is Separate Property?
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Separate property belongs to one spouse alone and includes assets owned before
            marriage, gifts received by one spouse, inheritances, and personal injury
            settlements (except for lost wages).
          </p>

          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <h4 className="font-bold text-gray-900 mb-3">Examples of Separate Property:</h4>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-2 mt-1"></i>
                <span>Property owned before the marriage</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-2 mt-1"></i>
                <span>Gifts given specifically to one spouse</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-2 mt-1"></i>
                <span>Inheritances received by one spouse</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-2 mt-1"></i>
                <span>Personal injury awards (excluding lost wages)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-2 mt-1"></i>
                <span>Property purchased with separate funds and kept separate</span>
              </li>
            </ul>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Why does this distinction matter? Because Texas intestacy laws treat community
            property and separate property very differently when determining who inherits.
            Understanding which category your assets fall into is essential for predicting
            how an intestate estate will be distributed.
          </p>
        </section>

        {/* Intestate Succession with Surviving Spouse Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Intestate Succession When There&apos;s a Surviving Spouse
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The distribution rules become more complex when the deceased was married.
            Texas law considers both the type of property (community vs. separate) and
            whether the deceased had children — and critically, whether those children are
            also children of the surviving spouse.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Distribution of Community Property
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Remember, each spouse already owns half of all community property. When one
            spouse dies intestate, we&apos;re only distributing their half. Here&apos;s how it works:
          </p>

          <div className="space-y-4 mb-8">
            <div className="bg-teal-50 p-5 rounded-lg border-l-4 border-teal-500">
              <h4 className="font-bold text-gray-900 mb-2">
                Scenario A: No Children or Descendants
              </h4>
              <p className="text-gray-700">
                The surviving spouse inherits the deceased spouse&apos;s entire half of community
                property. Combined with their own half, the surviving spouse now owns 100% of
                what was community property.
              </p>
            </div>
            <div className="bg-teal-50 p-5 rounded-lg border-l-4 border-teal-500">
              <h4 className="font-bold text-gray-900 mb-2">
                Scenario B: Children Who Are Also Children of the Surviving Spouse
              </h4>
              <p className="text-gray-700">
                The surviving spouse inherits the deceased spouse&apos;s entire half of community
                property. The children inherit nothing from community property (though they may
                inherit from separate property).
              </p>
            </div>
            <div className="bg-amber-50 p-5 rounded-lg border-l-4 border-amber-500">
              <h4 className="font-bold text-gray-900 mb-2">
                Scenario C: Children Who Are NOT Children of the Surviving Spouse (Blended Families)
              </h4>
              <p className="text-gray-700">
                <strong>This is where it gets complicated.</strong> The deceased spouse&apos;s half
                of community property goes entirely to their children — the surviving spouse
                receives nothing from the deceased&apos;s share. The surviving spouse keeps only
                their own half.
              </p>
            </div>
          </div>

          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg mb-8">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-error-warning-line mr-2 text-red-500"></i>
              Critical Warning for Blended Families
            </h4>
            <p className="text-gray-700">
              If you&apos;re in a blended family situation — where one spouse has children from a
              previous relationship — intestacy can create serious problems. The surviving
              spouse may lose half of assets they considered &quot;theirs&quot; to stepchildren.
              This is one of the most compelling reasons to create a will, especially in
              second marriages.
            </p>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Distribution of Separate Property
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Separate property follows different rules, and the type of property matters:
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="px-6 py-4 text-left font-semibold">Family Situation</th>
                  <th className="px-6 py-4 text-left font-semibold">Separate Personal Property</th>
                  <th className="px-6 py-4 text-left font-semibold">Separate Real Property</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-medium">No children or descendants</td>
                  <td className="px-6 py-4">Spouse inherits all</td>
                  <td className="px-6 py-4">Spouse inherits all</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 font-medium">Children (all from this marriage)</td>
                  <td className="px-6 py-4">Spouse: 1/3, Children: 2/3</td>
                  <td className="px-6 py-4">
                    Spouse: Life estate in 1/3, Children: Full ownership of 2/3 (plus remainder of spouse&apos;s 1/3)
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-medium">Children from previous relationship</td>
                  <td className="px-6 py-4">Spouse: 1/3, Children: 2/3</td>
                  <td className="px-6 py-4">
                    Spouse: Life estate in 1/3, Children: Full ownership of 2/3 (plus remainder)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Notice the term &quot;life estate&quot; for real property. This means the surviving spouse
            can use and live in the property during their lifetime, but they don&apos;t own it
            outright. When they pass away, full ownership transfers to the children. This can
            create complications if the surviving spouse wants to sell the property —
            they&apos;ll need the children&apos;s cooperation.
          </p>
        </section>

        {/* Intestate Succession Without Spouse Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Intestate Succession When There&apos;s No Surviving Spouse
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=family%20tree%20diagram%20showing%20inheritance%20flow%20from%20deceased%20person%20to%20children%20grandchildren%20parents%20siblings%20with%20legal%20document%20overlay%20professional%20illustration%20warm%20earth%20tones%20clean%20design&width=800&height=500&seq=intestacy-no-spouse-004&orientation=landscape"
            alt="Family tree showing inheritance without surviving spouse"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When someone dies intestate without a surviving spouse, the distribution follows
            a specific order of priority. Texas law works down this list until it finds living
            relatives:
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 text-white rounded-full mr-4 flex-shrink-0 font-bold">
                1
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md flex-1">
                <h4 className="font-bold text-gray-900 mb-2">Children and Their Descendants</h4>
                <p className="text-gray-700 text-sm">
                  If the deceased had children, they inherit everything equally. If a child
                  predeceased the parent but left their own children (the deceased&apos;s
                  grandchildren), those grandchildren inherit their parent&apos;s share.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 text-white rounded-full mr-4 flex-shrink-0 font-bold">
                2
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md flex-1">
                <h4 className="font-bold text-gray-900 mb-2">Parents</h4>
                <p className="text-gray-700 text-sm">
                  If there are no children or descendants, the estate goes to the deceased&apos;s
                  parents. If both parents are living, they share equally. If only one parent
                  survives, they receive half, and the other half goes to siblings.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 text-white rounded-full mr-4 flex-shrink-0 font-bold">
                3
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md flex-1">
                <h4 className="font-bold text-gray-900 mb-2">Siblings and Their Descendants</h4>
                <p className="text-gray-700 text-sm">
                  If no parents survive, siblings inherit. If a sibling predeceased the person,
                  their children (nieces and nephews) inherit that sibling&apos;s share.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 text-white rounded-full mr-4 flex-shrink-0 font-bold">
                4
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md flex-1">
                <h4 className="font-bold text-gray-900 mb-2">Grandparents and Their Descendants</h4>
                <p className="text-gray-700 text-sm">
                  If no closer relatives exist, the estate is divided between the maternal and
                  paternal sides, going to grandparents or their descendants (aunts, uncles,
                  cousins).
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-400 text-white rounded-full mr-4 flex-shrink-0 font-bold">
                5
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md flex-1">
                <h4 className="font-bold text-gray-900 mb-2">Escheat to the State</h4>
                <p className="text-gray-700 text-sm">
                  If absolutely no relatives can be found after a diligent search, the estate
                  &quot;escheats&quot; to the State of Texas. This is rare but does happen.
                </p>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This hierarchy explains why intestacy can lead to unexpected outcomes. A deceased
            person might have wanted their estate to go to a close friend, a charity, or a
            more distant relative who was particularly close to them. Under intestacy, none
            of these wishes matter — the law follows its predetermined order regardless of
            personal relationships.
          </p>
        </section>

        {/* Middle Lead Capture Form */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h2 className="text-3xl font-bold mb-4">
                  Dealing With an Intestate Estate?
                </h2>
                <p className="text-gray-300 mb-6">
                  We specialize in helping families sell inherited properties — even when
                  there&apos;s no will and multiple heirs are involved. Get a fair cash offer
                  with no obligation.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Work with multiple heirs</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Handle complex title situations</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Buy properties in any condition</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Fast, fair cash offers</span>
                  </li>
                </ul>
              </div>
              <div>
                <IntestacyLawsForm
                  formId="middle-intestacy-form"
                  title="Get Your Cash Offer"
                  subtitle="No obligation, no pressure"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Special Situations Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Special Situations Under Texas Intestacy Laws
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Texas intestacy laws include provisions for various special circumstances.
            Understanding these can help you anticipate how specific situations might be
            handled.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=diverse%20family%20situations%20including%20adopted%20children%20stepchildren%20and%20half%20siblings%20gathered%20together%20representing%20complex%20family%20inheritance%20scenarios%20warm%20professional%20photography%20natural%20lighting&width=800&height=500&seq=intestacy-special-005&orientation=landscape"
            alt="Diverse family situations in inheritance"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Adopted Children
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Under Texas law, legally adopted children have the same inheritance rights as
            biological children. They can inherit from their adoptive parents (and their
            adoptive parents&apos; relatives) just as if they were born into the family.
            However, adoption typically severs inheritance rights from biological parents,
            with some exceptions for stepparent adoptions.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Half-Siblings
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Half-siblings (sharing one parent) are treated the same as full siblings under
            Texas intestacy laws. They inherit equally with full siblings when siblings are
            entitled to inherit.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Posthumous Children
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A child conceived before but born after the parent&apos;s death (a posthumous
            child) has full inheritance rights, provided they&apos;re born within a reasonable
            time and can be proven to be the deceased&apos;s child.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Children Born Outside of Marriage
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Children born outside of marriage can inherit from their mother automatically.
            To inherit from their father, paternity must be established — either through
            acknowledgment, court order, or genetic testing. Once paternity is established,
            the child has full inheritance rights.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Stepchildren
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            <strong>Stepchildren have no automatic inheritance rights under Texas intestacy
            laws.</strong> Unless legally adopted, a stepchild cannot inherit from a
            stepparent who dies without a will — even if they were raised by that stepparent
            from infancy. This is one of the most common situations where intestacy produces
            results contrary to what the deceased likely would have wanted.
          </p>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-lightbulb-line mr-2 text-teal-600"></i>
              Planning Tip
            </h4>
            <p className="text-gray-700">
              If you want stepchildren to inherit from you, you must create a will specifically
              naming them as beneficiaries. Intestacy laws will not provide for them,
              regardless of how close your relationship was.
            </p>
          </div>
        </section>

        {/* The Homestead Exception Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The Texas Homestead Exception
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Texas has strong homestead protections that affect how the family home is
            treated in intestate succession. Even when intestacy laws would otherwise give
            children ownership of real property, the surviving spouse has special rights to
            the homestead.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Specifically, the surviving spouse has the right to occupy the homestead for
            life, regardless of who technically owns it under intestacy laws. This &quot;homestead
            right&quot; cannot be defeated by the children&apos;s ownership interest. The
            surviving spouse can continue living in the home until they die, remarry, or
            voluntarily abandon the property.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            However, this protection only applies to occupancy — not to selling the
            property. If the surviving spouse wants to sell the homestead and the children
            own an interest in it, all parties must agree to the sale. This can create
            difficult situations, especially if the surviving spouse needs to sell to
            access equity or move to assisted living. For families dealing with{' '}
            <a
              href="/sell-distressed-property"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              distressed property situations
            </a>
            , these complications can make an already difficult situation even more
            challenging.
          </p>
        </section>

        {/* What Intestacy Means for Real Estate Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What Intestacy Means for Real Estate in El Paso
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20southwestern%20style%20home%20in%20el%20paso%20texas%20with%20franklin%20mountains%20in%20background%20showing%20real%20estate%20property%20with%20multiple%20family%20members%20discussing%20inheritance%20warm%20golden%20hour%20lighting%20professional%20photography&width=800&height=500&seq=intestacy-realestate-006&orientation=landscape"
            alt="El Paso home with inheritance considerations"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For many El Paso families, the family home is the most valuable asset in an
            estate. When someone dies intestate, real property often becomes the most
            complicated asset to handle. Here&apos;s why:
          </p>

          <div className="space-y-4 mb-8">
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-group-line text-teal-600 mr-2"></i>
                Multiple Owners
              </h4>
              <p className="text-gray-700 text-sm">
                Intestacy often results in multiple heirs owning the property together as
                &quot;tenants in common.&quot; Each heir owns a percentage, and all must agree
                to sell. If one heir refuses, the others may need to pursue a partition
                action in court.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-file-shield-2-line text-teal-600 mr-2"></i>
                Title Issues
              </h4>
              <p className="text-gray-700 text-sm">
                Without probate, the deceased&apos;s name remains on the deed. Title companies
                won&apos;t insure the property, and most buyers won&apos;t purchase without clear
                title. This can leave property stuck in limbo for years.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-money-dollar-circle-line text-teal-600 mr-2"></i>
                Ongoing Costs
              </h4>
              <p className="text-gray-700 text-sm">
                Property taxes, insurance, and maintenance don&apos;t stop just because ownership
                is unclear. Someone has to pay these costs, which can create conflict among
                heirs about who&apos;s responsible.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-home-gear-line text-teal-600 mr-2"></i>
                Deferred Maintenance
              </h4>
              <p className="text-gray-700 text-sm">
                When ownership is disputed or unclear, properties often fall into disrepair.
                No one wants to invest in a property they might not ultimately own, leading to
                declining value.
              </p>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            These complications are why many families with intestate estates turn to{' '}
            <a
              href="/land-buyers"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              cash land buyers
            </a>{' '}
            and property investors who specialize in complicated title situations. At TREX
            RE LLC, we regularly work with families dealing with intestate estates,
            helping them navigate the legal complexities and find solutions that work for
            everyone involved.
          </p>
        </section>

        {/* The Probate Process for Intestate Estates Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The Probate Process for Intestate Estates
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Even without a will, most intestate estates still need to go through probate to
            legally transfer assets. The process is similar to probating a will, with some
            key differences:
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full mr-4 flex-shrink-0">
                <span className="text-teal-600 font-bold">1</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  File Application for Administration
                </p>
                <p className="text-gray-600 text-sm">
                  Instead of filing a will, an heir files an application asking the court to
                  appoint an administrator for the estate.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full mr-4 flex-shrink-0">
                <span className="text-teal-600 font-bold">2</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Determine Heirship</p>
                <p className="text-gray-600 text-sm">
                  The court must determine who the legal heirs are. This may require testimony
                  from family members and others who knew the deceased.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full mr-4 flex-shrink-0">
                <span className="text-teal-600 font-bold">3</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Appoint Administrator</p>
                <p className="text-gray-600 text-sm">
                  The court appoints an administrator (similar to an executor) to manage the
                  estate. Texas law has a priority list for who can serve.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full mr-4 flex-shrink-0">
                <span className="text-teal-600 font-bold">4</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Post Bond (Usually)</p>
                <p className="text-gray-600 text-sm">
                  Unlike executors named in wills (who can often serve without bond), administrators
                  typically must post a bond to protect the estate.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full mr-4 flex-shrink-0">
                <span className="text-teal-600 font-bold">5</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Administer Estate</p>
                <p className="text-gray-600 text-sm">
                  The administrator inventories assets, pays debts, and distributes property
                  according to intestacy laws.
                </p>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Intestate administration is often more expensive and time‑consuming than probating
            a will. The bond requirement, heirship determination, and potential for disputes
            among heirs all add complexity and cost. This is another reason why having a will
            is so important — it simplifies the process for your loved ones.
          </p>
        </section>

        {/* Alternatives to Full Administration Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Alternatives to Full Intestate Administration
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Texas offers some simplified procedures that may be available for smaller or
            less complicated intestate estates:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-file-text-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Small Estate Affidavit</h4>
              <p className="text-gray-700 text-sm mb-3">
                For estates valued at $75,000 or less (excluding homestead and exempt property),
                heirs may be able to use a Small Estate Affidavit to collect assets without full
                administration.
              </p>
              <p className="text-gray-600 text-xs">
                <strong>Requirements:</strong> 30 days must pass after death, no pending
                administration, all heirs must sign, and debts must not exceed assets.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-file-shield-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Affidavit of Heirship</h4>
              <p className="text-gray-700 text-sm mb-3">
                For real property, an Affidavit of Heirship can help establish ownership without
                full probate. It&apos;s signed by someone familiar with the family (not an heir) and
                recorded in property records.
              </p>
              <p className="text-gray-600 text-xs">
                <strong>Note:</strong> This doesn&apos;t transfer title but can help title companies
                insure the property for sale.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-scales-2-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Determination of Heirship</h4>
              <p className="text-gray-700 text-sm mb-3">
                A court proceeding that officially determines who the heirs are and their respective
                shares. This creates a court order that can be used to transfer property.
              </p>
              <p className="text-gray-600 text-xs">
                <strong>Best for:</strong> Real property transfers when full administration isn&apos;t necessary.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-hand-coin-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Sell to Cash Buyer</h4>
              <p className="text-gray-700 text-sm mb-3">
                For families who need to sell inherited property quickly, working with a{' '}
                <a href="/rental-property" className="text-teal-600 hover:text-teal-700">
                  cash buyer experienced with rental and inherited properties
                </a>{' '}
                can often resolve complicated situations.
              </p>
              <p className="text-gray-600 text-xs">
                <strong>Advantage:</strong> Cash buyers can often work with title companies to find
                solutions that traditional buyers can&apos;t.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions About Texas Intestacy Laws
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Does Texas have an inheritance tax?
              </h3>
              <p className="text-gray-700">
                No, Texas does not have a state inheritance tax or estate tax. However, federal
                estate tax may apply to very large estates (over $12.92 million for individuals in
                2023). Most families don&apos;t need to worry about estate taxes.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I disinherit my spouse under Texas law?
              </h3>
              <p className="text-gray-700">
                You cannot completely disinherit a spouse in Texas. Even with a will, your spouse
                has rights to their half of community property and homestead rights. However,
                you can control the distribution of your separate property through a will.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if the deceased had debts?
              </h3>
              <p className="text-gray-700">
                Debts must be paid from the estate before heirs receive their inheritance.
                If debts exceed assets, heirs receive nothing, but they&apos;re generally not
                personally responsible for the deceased&apos;s debts (with some exceptions for
                secured debts like mortgages).
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How long does intestate probate take?
              </h3>
              <p className="text-gray-700">
                Simple intestate estates might be resolved in 6-12 months. Complex estates with
                multiple heirs, disputes, or title issues can take years. Working with experienced
                professionals can help expedite the process.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can unmarried partners inherit under intestacy?
              </h3>
              <p className="text-gray-700">
                No. Texas intestacy laws only recognize legal spouses and blood relatives.
                Unmarried partners, regardless of how long they&apos;ve been together, have no
                inheritance rights under intestacy. A will is essential for unmarried couples who
                want to provide for each other.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What happens if heirs can&apos;t agree on what to do with property?
              </h3>
              <p className="text-gray-700">
                When heirs can&apos;t agree, any heir can file a &quot;partition action&quot; asking the
                court to divide the property or force a sale. This is expensive and time‑consuming,
                which is why many families prefer to work with{' '}
                <a href="/sell-apartments" className="text-teal-600 hover:text-teal-700">
                  cash buyers who can purchase apartment buildings and multi‑family properties
                </a>{' '}
                or other inherited real estate to avoid court battles.
              </p>
            </div>
          </div>
        </section>

        {/* Protecting Your Family Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Protecting Your Family: The Case for Estate Planning
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            After reading about Texas intestacy laws, one thing should be clear: <strong>having a
            will gives you control that intestacy laws simply cannot provide</strong>. Here&apos;s
            what a will allows you to do that intestacy doesn&apos;t:
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Choose exactly who inherits your property</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Provide for stepchildren and unmarried partners</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Leave assets to friends and charities</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Exclude family members you&apos;re estranged from</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Name a guardian for minor children</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Choose your executor (avoiding bond requirements)</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Create trusts for minor children or special needs beneficiaries</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Simplify the probate process for your family</span>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Creating a will doesn&apos;t have to be complicated or expensive. Many attorneys offer
            basic will packages at reasonable rates, and online services can help with simple
            situations. The peace of mind knowing your wishes will be honored is invaluable.
          </p>
        </section>

        {/* Final CTA Section */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
                <i className="ri-hand-heart-line text-4xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                We&apos;re Here to Help Your Family
              </h2>
              <p className="text-xl text-teal-100 max-w-2xl mx-auto">
                Dealing with an intestate estate can be overwhelming. If you&apos;ve inherited
                property and need to sell — regardless of how complicated the situation is —
                we can help find a solution.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8">
              <IntestacyLawsForm
                formId="final-intestacy-form"
                title="Get Your Free Consultation"
                subtitle="No obligation, compassionate service"
              />
            </div>

            <div className="mt-8 text-center">
              <p className="text-teal-100 mb-4">Prefer to talk? Call us directly:</p>
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                <i className="ri-phone-line mr-2"></i>(915) 206-5814
              </a>
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="/elpaso-tx/what-happens-if-you-dont-probate-will-texas"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-error-warning-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">
                  What Happens If You Don&apos;t Probate a Will
                </p>
                <p className="text-sm text-gray-600">Consequences and solutions</p>
              </div>
            </a>
            <a
              href="/elpaso-tx/texas-probate-guide"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-scales-3-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Texas Probate Guide</p>
                <p className="text-sm text-gray-600">Complete probate process explained</p>
              </div>
            </a>
            <a
              href="/inherited-homes"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-heart-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Selling Inherited Homes</p>
                <p className="text-sm text-gray-600">Options for inherited property</p>
              </div>
            </a>
            <a
              href="/get-cash-offer-for-my-property"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-money-dollar-circle-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Get Cash Offer</p>
                <p className="text-sm text-gray-600">Request your no-obligation offer</p>
              </div>
            </a>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}
